import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Typography,
  Box,
  Button,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  LinearProgress,
  InputAdornment,
} from "@mui/material";
import { LineChart } from "@mui/x-charts/LineChart";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CryptoJS from "crypto-js";
import "./AdminPanel.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const AES_SECRET_KEY = process.env.AES_SECRET_KEY;
const AES_IV = process.env.AES_IV;

const AdminPanel = ({ theme = "dark" }) => {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [users, setUsers] = useState([]);
  const [bots, setBots] = useState({
    complianceBot: {
      enabled: true,
      status: "Running",
      department: "Compliance",
    },
    financialBot: { enabled: false, status: "Idle", department: "Financial" },
    auditBot: { enabled: true, status: "Running", department: "Audit" },
  });
  const [logs, setLogs] = useState([
    {
      id: "log1",
      user: "System",
      action: "System initialized",
      timestamp: new Date().toISOString(),
      details: "Admin panel loaded",
    },
  ]);
  const [notifications, setNotifications] = useState([
    {
      id: "notif1",
      message: "System maintenance scheduled",
      timestamp: new Date().toISOString(),
    },
  ]);
  const [sortConfig, setSortConfig] = useState({
    key: "full_name",
    direction: "asc",
  });
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [editUser, setEditUser] = useState(null);
  const [newUser, setNewUser] = useState({
    email: "",
    full_name: "",
    is_superuser: "False",
    is_active: "True",
    password: "",
    tid_token: "",
    department: "Compliance",
  });
  const [openUserModal, setOpenUserModal] = useState(false);
  const [openNotifModal, setOpenNotifModal] = useState(false);
  const [notifMessage, setNotifMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [isEntitiesLoading, setIsEntitiesLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tid, setTid] = useState("");

  const departments = ["Compliance", "Audit", "Financial"];

  const encryptData = (text) => {
    const key = CryptoJS.enc.Utf8.parse(AES_SECRET_KEY);
    const iv = CryptoJS.enc.Utf8.parse(AES_IV);
    const encrypted = CryptoJS.AES.encrypt(text, key, {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });
    return encrypted.toString();
  };

  const decryptData = (ciphertext) => {
    try {
      if (!ciphertext) return "";
      const key = CryptoJS.enc.Utf8.parse(AES_SECRET_KEY);
      const iv = CryptoJS.enc.Utf8.parse(AES_IV);
      const decrypted = CryptoJS.AES.decrypt(ciphertext, key, {
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      });
      const plaintext = decrypted.toString(CryptoJS.enc.Utf8);
      if (!plaintext) {
        console.warn("Decryption resulted in empty string for:", ciphertext);
        return ciphertext;
      }
      return plaintext;
    } catch (err) {
      console.error("Decryption error:", err, "for ciphertext:", ciphertext);
      return ciphertext;
    }
  };

  const fetchUsers = async () => {
    try {
      setIsEntitiesLoading(true);
      const token = localStorage.getItem("authToken");
      if (!token) throw new Error("No authentication token found.");
      const response = await axios.get(`${API_BASE_URL}/users/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const decryptedUsers = (response.data.data || []).map((user) => ({
        ...user,
        email: decryptData(user.email),
        full_name: decryptData(user.full_name),
      }));
      setUsers(decryptedUsers);
      setIsEntitiesLoading(false);
    } catch (err) {
      console.error("Failed to fetch users:", err);
      setUsers([]);
      setIsEntitiesLoading(false);
      const errorMessage =
        err.response?.status === 404
          ? "The users endpoint was not found. Please check the API configuration."
          : "Failed to fetch users: " +
            (err.response?.data?.message || err.message);
      toast.error(errorMessage, {
        position: "bottom-right",
        autoClose: 3000,
        className: "toast-error",
      });
    }
  };

  const createUser = async (userData) => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      if (!token) throw new Error("No authentication token found.");
      const encryptedUserData = {
        ...userData,
        email: encryptData(userData.email),
        full_name: encryptData(userData.full_name),
        password: encryptData(userData.password),
        tid_token: encryptData(userData.tid_token),
      };
      const response = await axios.post(
        `${API_BASE_URL}/users/signup`,
        encryptedUserData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (err) {
      console.error("Error creating user:", err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateUser = async (userId, userData) => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      if (!token) throw new Error("No authentication token found.");
      const encryptedUserData = {
        ...userData,
        email: encryptData(userData.email),
        full_name: encryptData(userData.full_name),
      };
      const response = await axios.patch(
        `${API_BASE_URL}/users/${userId}`,
        encryptedUserData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (err) {
      console.error("Error updating user:", err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteUser = async (userId) => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      if (!token) throw new Error("No authentication token found.");
      const response = await axios.delete(`${API_BASE_URL}s/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (err) {
      console.error("Error deleting user:", err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const createTidToken = async (isSuperuser) => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      if (!token) throw new Error("No authentication token found.");
      const response = await axios.post(
        `${API_BASE_URL}/private/tids/`,
        {
          is_superuser: isSuperuser,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      setTid(response.data.token);
      setNewUser((prev) => ({ ...prev, tid_token: response.data.token }));
      toast.success("Token ID generated successfully.", {
        position: "bottom-right",
        autoClose: 3000,
        className: "toast-success",
      });
    } catch (err) {
      console.error("Error creating TID token:", err);
      toast.error(
        "Failed to generate Token ID: " +
          (err.response?.data?.message || err.message),
        {
          position: "bottom-right",
          autoClose: 3000,
          className: "toast-error",
        }
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleTabChange = (event, newValue) => setTab(newValue);

  const handleSort = (key) => {
    const direction =
      sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc";
    setSortConfig({ key, direction });
    setUsers((prev) =>
      [...prev].sort((a, b) => {
        const aVal = a[key] || "";
        const bVal = b[key] || "";
        return aVal < bVal
          ? direction === "asc"
            ? -1
            : 1
          : aVal > bVal
          ? direction === "asc"
            ? 1
            : -1
          : 0;
      })
    );
  };

  const handleSelectUser = (id) => {
    setSelectedUsers((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleBulkAction = (action) => {
    toast.info(
      `Bulk ${action} for ${selectedUsers.length} users would be implemented here.`,
      {
        position: "bottom-right",
        autoClose: 3000,
      }
    );
    setSelectedUsers([]);
  };

  const handleAddUser = async () => {
    if (
      !newUser.email ||
      !newUser.full_name ||
      !newUser.password ||
      !newUser.tid_token
    ) {
      toast.error("Email, full name, password, and Token ID are required.", {
        position: "bottom-right",
        autoClose: 3000,
        className: "toast-error",
      });
      return;
    }
    try {
      const userData = {
        email: newUser.email,
        full_name: newUser.full_name,
        password: newUser.password,
        tid_token: newUser.tid_token,
        is_superuser: newUser.is_superuser,
        is_active: newUser.is_active,
        department: newUser.department,
      };

      await createUser(userData);
      fetchUsers();
      setOpenUserModal(false);
      setNewUser({
        email: "",
        full_name: "",
        is_superuser: "False",
        is_active: "True",
        password: "",
        tid_token: "",
        department: "Compliance",
      });
      setTid("");
      setLogs((prev) => [
        ...prev,
        {
          id: `log${prev.length + 1}`,
          user: "System",
          action: "User added",
          timestamp: new Date().toISOString(),
          details: `Added ${newUser.full_name}`,
        },
      ]);
      toast.success("New user added successfully.", {
        position: "bottom-right",
        autoClose: 3000,
        className: "toast-success",
      });
    } catch (err) {
      toast.error(
        "Failed to create user: " +
          (err.response?.data?.message || err.message),
        {
          position: "bottom-right",
          autoClose: 3000,
          className: "toast-error",
        }
      );
    }
  };

  const handleEditUser = async () => {
    if (!editUser.email || !editUser.full_name) {
      toast.error("Email and full name are required.", {
        position: "bottom-right",
        autoClose: 3000,
        className: "toast-error",
      });
      return;
    }
    try {
      await updateUser(editUser.id, editUser);
      fetchUsers();
      setOpenUserModal(false);
      setEditUser(null);
      setLogs((prev) => [
        ...prev,
        {
          id: `log${prev.length + 1}`,
          user: "System",
          action: "User updated",
          timestamp: new Date().toISOString(),
          details: `Updated ${editUser.full_name}`,
        },
      ]);
      toast.success("User settings saved successfully.", {
        position: "bottom-right",
        autoClose: 3000,
        className: "toast-success",
      });
    } catch (err) {
      toast.error(
        "Failed to update user: " +
          (err.response?.data?.message || err.message),
        {
          position: "bottom-right",
          autoClose: 3000,
          className: "toast-error",
        }
      );
    }
  };

  const handleDeleteUser = async (id) => {
    const user = users.find((u) => u.id === id);
    try {
      await deleteUser(id);
      setUsers((prev) => prev.filter((u) => u.id !== id));
      setLogs((prev) => [
        ...prev,
        {
          id: `log${prev.length + 1}`,
          user: "System",
          action: "User deleted",
          timestamp: new Date().toISOString(),
          details: `Deleted ${user.full_name}`,
        },
      ]);
      toast.success("User removed successfully.", {
        position: "bottom-right",
        autoClose: 3000,
        className: "toast-success",
      });
    } catch (err) {
      toast.error(
        "Failed to delete user: " +
          (err.response?.data?.message || err.message),
        {
          position: "bottom-right",
          autoClose: 3000,
          className: "toast-error",
        }
      );
    }
  };

  const handleBotToggle = (botId) => {
    setBots((prev) => {
      const newEnabled = !prev[botId].enabled;
      const newStatus = newEnabled ? "Running" : "Idle";
      setLogs((prevLogs) => [
        ...prevLogs,
        {
          id: `log${prevLogs.length + 1}`,
          user: "System",
          action: "Bot toggled",
          timestamp: new Date().toISOString(),
          details: `${botId.replace("Bot", " Bot")} ${
            newEnabled ? "enabled" : "disabled"
          }`,
        },
      ]);
      return {
        ...prev,
        [botId]: { ...prev[botId], enabled: newEnabled, status: newStatus },
      };
    });
  };

  const handleResetBots = () => {
    setBots({
      complianceBot: {
        enabled: true,
        status: "Running",
        department: "Compliance",
      },
      financialBot: { enabled: false, status: "Idle", department: "Financial" },
      auditBot: { enabled: true, status: "Running", department: "Audit" },
    });
    setLogs((prev) => [
      ...prev,
      {
        id: `log${prev.length + 1}`,
        user: "System",
        action: "Bots reset",
        timestamp: new Date().toISOString(),
        details: "Bot configurations restored",
      },
    ]);
    toast.success("Bot configurations restored.", {
      position: "bottom-right",
      autoClose: 1500,
      className: "toast-success",
    });
  };

  const handleSendNotification = () => {
    if (!notifMessage) {
      toast.error("Message is required.", {
        position: "bottom-right",
        autoClose: 3000,
        className: "toast-error",
      });
      return;
    }
    setNotifications((prev) => [
      ...prev,
      {
        id: `notif${prev.length + 1}`,
        message: notifMessage,
        timestamp: new Date().toISOString(),
      },
    ]);
    setOpenNotifModal(false);
    setNotifMessage("");
    setLogs((prev) => [
      ...prev,
      {
        id: `log${prev.length + 1}`,
        user: "System",
        action: "Notification sent",
        timestamp: new Date().toISOString(),
        details: "System-wide alert sent",
      },
    ]);
    toast.success("System-wide alert sent.", {
      position: "bottom-right",
      autoClose: 1500,
      className: "toast-success",
    });
  };

const chartData = useMemo(() => {
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
    ];
    return {
      months,
      adminData: months.map(() => Math.floor(Math.random() * 20 + 10)),
      userData: months.map(() => Math.floor(Math.random() * 50 + 20)),
      botData: months.map(() => Math.floor(Math.random() * 15 + 5)),
    };
  }, []);

  const getRoleFromUser = (user) =>
    user.is_superuser === "True" ? "Admin" : "User";

  const handleCopyTid = () => {
    navigator.clipboard
      .writeText(tid)
      .then(() => {
        toast.success("Token ID copied to clipboard!", {
          position: "bottom-right",
          autoClose: 1500,
          className: "toast-success",
        });
      })
      .catch((err) => {
        console.error("Failed to copy TID:", err);
        toast.error("Failed to copy Token ID.", {
          position: "bottom-right",
          autoClose: 3000,
          className: "toast-error",
        });
      });
  };

  return (
    <Box className={`admin-panel-container ${theme}-theme`}>
      <style>
        {theme === 'dark' ? `
          .MuiChartsTooltip-root,
          .MuiChartsTooltip-paper,
          .MuiPaper-root.MuiChartsTooltip-root {
            background-color: #1f2937 !important;
            color: #ffffff !important;
            border: 1px solid rgba(255, 255, 255, 0.2) !important;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5) !important;
          }
          .MuiChartsTooltip-table,
          .MuiChartsTooltip-table * {
            background-color: transparent !important;
            color: #ffffff !important;
          }
          .MuiChartsTooltip-row,
          .MuiChartsTooltip-row * {
            background-color: transparent !important;
            color: #ffffff !important;
          }
          .MuiChartsTooltip-cell,
          .MuiChartsTooltip-labelCell,
          .MuiChartsTooltip-valueCell {
            color: #ffffff !important;
            background-color: transparent !important;
          }
          .MuiChartsTooltip-mark {
            border-color: rgba(255, 255, 255, 0.3) !important;
          }
          .MuiPopper-root .MuiPaper-root {
            background-color: #1f2937 !important;
            color: #ffffff !important;
          }
          .MuiPopper-root td,
          .MuiPopper-root th {
            color: #ffffff !important;
            background-color: transparent !important;
          }
          .MuiPopper-root tr {
            background-color: transparent !important;
          }
          .MuiPopper-root table {
            background-color: transparent !important;
            color: #ffffff !important;
          }
          /* Target any white backgrounds in popper */
          .MuiPopper-root [style*="background-color: rgb(255, 255, 255)"],
          .MuiPopper-root [style*="background-color: white"],
          .MuiPopper-root [style*="background-color:#fff"] {
            background-color: #1f2937 !important;
            color: #ffffff !important;
          }
        ` : ''}
      </style>
      <div className="admin-panel-particles">
        {[...Array(30)].map((_, i) => (
          <div
            key={i}
            className="admin-panel-particle"
            style={{
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 10}s`,
              animationDuration: `${15 + Math.random() * 10}s`,
            }}
          />
        ))}
      </div>
      
      <Box className="admin-panel-content">
        <Box sx={{ mb: 3 }}>
          <Tabs
            value={tab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            className="admin-panel-tabs"
            sx={{
              '& .MuiTab-root': {
                color: theme === 'dark' ? '#ffffff' : '#1f2937',
              },
              '& .Mui-selected': {
                color: theme === 'dark' ? '#f093fb !important' : '#f093fb !important',
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#f093fb',
              }
            }}
          >
            <Tab label="Dashboard" />
            <Tab label="Users" />
            <Tab label="Bots" />
            <Tab label="Notifications" />
          </Tabs>
        </Box>

        <Box className="admin-panel-tab-content">
          {isEntitiesLoading && <LinearProgress sx={{ mb: 2 }} />}
          
          {tab === 0 && (
            <Box className="card">
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: theme === 'dark' ? '#ffffff' : '#1f2937' }}>
                System Overview
              </Typography>
              <Box className="admin-dashboard-grid">
                <Box className="admin-dashboard-chart card">
                 <LineChart
                    xAxis={[{ data: chartData.months, scaleType: "band" }]}
                    series={[
                      {
                        data: chartData.adminData,
                        label: "Admins",
                        color: "#f093fb",
                      },
                      {
                        data: chartData.userData,
                        label: "Users",
                        color: "#10b981",
                      },
                      {
                        data: chartData.botData,
                        label: "Bots",
                        color: "#3b82f6",
                      },
                    ]}
                    height={400}
                    grid={{ vertical: true, horizontal: true }}
                    sx={{
                      '& .MuiChartsAxis-tickLabel': {
                        fill: theme === 'dark' ? '#ffffff !important' : '#1f2937 !important',
                      },
                      '& .MuiChartsAxis-line': {
                        stroke: theme === 'dark' ? '#ffffff' : '#1f2937',
                      },
                      '& .MuiChartsAxis-tick': {
                        stroke: theme === 'dark' ? '#ffffff' : '#1f2937',
                      },
                      '& .MuiChartsLegend-series text': {
                        fill: theme === 'dark' ? '#ffffff !important' : '#1f2937 !important',
                      },
                    }}
                    slotProps={{
                      popper: {
                        sx: {
                          '& .MuiChartsTooltip-root': {
                            backgroundColor: theme === 'dark' ? 'rgba(31, 41, 55, 0.98) !important' : 'rgba(255, 255, 255, 0.98) !important',
                            color: theme === 'dark' ? '#ffffff !important' : '#1f2937 !important',
                            border: theme === 'dark' ? '1px solid rgba(255, 255, 255, 0.2) !important' : '1px solid rgba(0, 0, 0, 0.1) !important',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3) !important',
                            borderRadius: '8px !important',
                            padding: '8px 12px !important',
                          },
                          '& .MuiChartsTooltip-table': {
                            backgroundColor: theme === 'dark' ? 'transparent !important' : 'transparent !important',
                          },
                          '& .MuiChartsTooltip-row': {
                            backgroundColor: theme === 'dark' ? 'transparent !important' : 'transparent !important',
                          },
                          '& .MuiChartsTooltip-cell': {
                            color: theme === 'dark' ? '#ffffff !important' : '#1f2937 !important',
                            backgroundColor: theme === 'dark' ? 'transparent !important' : 'transparent !important',
                          },
                          '& .MuiChartsTooltip-mark': {
                            border: theme === 'dark' ? '2px solid rgba(255, 255, 255, 0.3) !important' : '2px solid rgba(0, 0, 0, 0.1) !important',
                          },
                          '& .MuiChartsTooltip-labelCell': {
                            color: theme === 'dark' ? '#ffffff !important' : '#1f2937 !important',
                            backgroundColor: theme === 'dark' ? 'transparent !important' : 'transparent !important',
                          },
                          '& .MuiChartsTooltip-valueCell': {
                            color: theme === 'dark' ? '#ffffff !important' : '#1f2937 !important',
                            backgroundColor: theme === 'dark' ? 'transparent !important' : 'transparent !important',
                          },
                          '& td': {
                            backgroundColor: theme === 'dark' ? 'transparent !important' : 'transparent !important',
                            color: theme === 'dark' ? '#ffffff !important' : '#1f2937 !important',
                          },
                          '& tr': {
                            backgroundColor: theme === 'dark' ? 'transparent !important' : 'transparent !important',
                          }
                        }
                      }
                    }}
                  />
                </Box>
                <Box className="admin-dashboard-stats card">
                  <Box className="admin-stat-item">
                    <Typography variant="body2" sx={{ color: theme === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(31, 41, 55, 0.7)' }}>Active Users</Typography>
                    <Typography variant="h4" sx={{ color: theme === 'dark' ? '#ffffff' : '#1f2937' }}>{users.filter((u) => u.is_active === "True").length}</Typography>
                  </Box>
                  <Box className="admin-stat-item">
                    <Typography variant="body2" sx={{ color: theme === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(31, 41, 55, 0.7)' }}>System Uptime</Typography>
                    <Typography variant="h4" sx={{ color: theme === 'dark' ? '#ffffff' : '#1f2937' }}>99.9%</Typography>
                  </Box>
                  <Box className="admin-stat-item">
                    <Typography variant="body2" sx={{ color: theme === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(31, 41, 55, 0.7)' }}>Recent Logs</Typography>
                    <Typography variant="h4" sx={{ color: theme === 'dark' ? '#ffffff' : '#1f2937' }}>{logs.length}</Typography>
                  </Box>
                  <Box className="admin-stat-item">
                    <Typography variant="body2" sx={{ color: theme === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(31, 41, 55, 0.7)' }}>Active Bots</Typography>
                    <Typography variant="h4" sx={{ color: theme === 'dark' ? '#ffffff' : '#1f2937' }}>
                      {Object.values(bots).filter((b) => b.status === "Running").length}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          )}

          {tab === 1 && (
            <Box>
              <Box className="admin-section-header">
                <Typography variant="h6" sx={{ fontWeight: 600, color: theme === 'dark' ? '#ffffff' : '#1f2937' }}>
                  User Management
                </Typography>
                <Button
                  variant="contained"
                  className="btn-contained"
                  onClick={() => setOpenUserModal(true)}
                  disabled={loading}
                  startIcon={<AddIcon />}
                >
                  Add User
                </Button>
              </Box>
              
              <Box className="admin-bulk-actions">
                <Button
                  variant="outlined"
                  className="btn-outlined"
                  onClick={() => handleBulkAction("Activate")}
                  disabled={selectedUsers.length === 0}
                >
                  Activate Selected
                </Button>
                <Button
                  variant="outlined"
                  className="btn-outlined"
                  onClick={() => handleBulkAction("Deactivate")}
                  disabled={selectedUsers.length === 0}
                >
                  Deactivate Selected
                </Button>
              </Box>

              {loading ? (
                <LinearProgress />
              ) : (
                <Box className="card" sx={{ overflowX: "auto" }}>
                  <Table className="table">
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={
                              selectedUsers.length === users.length &&
                              users.length > 0
                            }
                            indeterminate={
                              selectedUsers.length > 0 &&
                              selectedUsers.length < users.length
                            }
                            onChange={() =>
                              setSelectedUsers(
                                selectedUsers.length === users.length
                                  ? []
                                  : users.map((u) => u.id)
                              )
                            }
                            sx={{
                              color: theme === 'dark' ? '#ffffff' : '#1f2937',
                              '&.Mui-checked': {
                                color: '#f093fb',
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ color: theme === 'dark' ? '#ffffff' : '#1f2937' }}>
                          <TableSortLabel
                            active={sortConfig.key === "is_active"}
                            direction={sortConfig.direction}
                            onClick={() => handleSort("is_active")}
                            sx={{
                              '& .MuiTableSortLabel-icon': {
                                color: theme === 'dark' ? '#ffffff !important' : '#1f2937 !important',
                              },
                              '&.Mui-active': {
                                color: theme === 'dark' ? '#ffffff' : '#1f2937',
                              },
                              '&:hover': {
                                color: theme === 'dark' ? '#ffffff' : '#1f2937',
                              }
                            }}
                          >
                            Status
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ color: theme === 'dark' ? '#ffffff' : '#1f2937' }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {users.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center" sx={{ color: theme === 'dark' ? '#ffffff' : '#1f2937' }}>
                            {error || "No users found."}
                          </TableCell>
                        </TableRow>
                      ) : (
                        users.map((user) => (
                          <TableRow key={user.id}>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedUsers.includes(user.id)}
                                onChange={() => handleSelectUser(user.id)}
                                sx={{
                                  color: theme === 'dark' ? '#ffffff' : '#1f2937',
                                  '&.Mui-checked': {
                                    color: '#f093fb',
                                  }
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ color: theme === 'dark' ? '#ffffff' : '#1f2937' }}>
                              {user.full_name || "Encrypted (Decryption Failed)"}
                            </TableCell>
                            <TableCell sx={{ color: theme === 'dark' ? '#ffffff' : '#1f2937' }}>{getRoleFromUser(user)}</TableCell>
                            <TableCell>
                              <Chip
                                label={
                                  user.is_active === "True"
                                    ? "Active"
                                    : "Inactive"
                                }
                                color={
                                  user.is_active === "True"
                                    ? "success"
                                    : "default"
                                }
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="outlined"
                                className="btn-outlined"
                                size="small"
                                onClick={() => {
                                  setEditUser({ ...user });
                                  setOpenUserModal(true);
                                }}
                                sx={{ mr: 1 }}
                                disabled={loading}
                              >
                                Edit
                              </Button>
                              <Button
                                variant="outlined"
                                className="btn-outlined"
                                size="small"
                                color="error"
                                onClick={() => handleDeleteUser(user.id)}
                                disabled={loading}
                              >
                                Delete
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </Box>
          )}

          {tab === 2 && (
            <Box>
              <Box className="admin-section-header">
                <Typography variant="h6" sx={{ fontWeight: 600, color: theme === 'dark' ? '#ffffff' : '#1f2937' }}>
                  Bot Oversight
                </Typography>
                <Button 
                  variant="outlined" 
                  className="btn-outlined"
                  onClick={handleResetBots}
                >
                  Reset Bots
                </Button>
              </Box>
              
              <Box className="card" sx={{ overflowX: "auto" }}>
                <Table className="table">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: theme === 'dark' ? '#ffffff' : '#1f2937' }}>Bot</TableCell>
                      <TableCell sx={{ color: theme === 'dark' ? '#ffffff' : '#1f2937' }}>Department</TableCell>
                      <TableCell sx={{ color: theme === 'dark' ? '#ffffff' : '#1f2937' }}>Status</TableCell>
                      <TableCell sx={{ color: theme === 'dark' ? '#ffffff' : '#1f2937' }}>Enabled</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(bots).map(([botId, bot]) => (
                      <TableRow key={botId}>
                        <TableCell sx={{ color: theme === 'dark' ? '#ffffff' : '#1f2937' }}>{botId.replace("Bot", " Bot")}</TableCell>
                        <TableCell sx={{ color: theme === 'dark' ? '#ffffff' : '#1f2937' }}>{bot.department}</TableCell>
                        <TableCell>
                          <Chip
                            label={bot.status}
                            color={
                              bot.status === "Running" ? "success" : "default"
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Checkbox
                            checked={bot.enabled}
                            onChange={() => handleBotToggle(botId)}
                            sx={{
                              color: theme === 'dark' ? '#ffffff' : '#1f2937',
                              '&.Mui-checked': {
                                color: '#f093fb',
                              }
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Box>
          )}

          {tab === 3 && (
            <Box>
              <Box className="admin-section-header">
                <Typography variant="h6" sx={{ fontWeight: 600, color: theme === 'dark' ? '#ffffff' : '#1f2937' }}>
                  Notifications
                </Typography>
                <Button
                  variant="contained"
                  className="btn-contained"
                  onClick={() => setOpenNotifModal(true)}
                >
                  Send Notification
                </Button>
              </Box>
              
              <Box className="card" sx={{ overflowX: "auto" }}>
                <Table className="table">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: theme === 'dark' ? '#ffffff' : '#1f2937' }}>Message</TableCell>
                      <TableCell sx={{ color: theme === 'dark' ? '#ffffff' : '#1f2937' }}>Timestamp</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {notifications.map((notif) => (
                      <TableRow key={notif.id}>
                        <TableCell sx={{ color: theme === 'dark' ? '#ffffff' : '#1f2937' }}>{notif.message}</TableCell>
                        <TableCell sx={{ color: theme === 'dark' ? '#ffffff' : '#1f2937' }}>
                          {new Date(notif.timestamp).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Box>
          )}
        </Box>

        {/* User Modal */}
        <Dialog
          open={openUserModal}
          onClose={() => {
            setOpenUserModal(false);
            setEditUser(null);
            setTid("");
            if (!editUser) {
              setNewUser({
                email: "",
                full_name: "",
                is_superuser: "False",
                is_active: "True",
                password: "",
                tid_token: "",
                department: "Compliance",
              });
            }
          }}
          maxWidth="sm"
          fullWidth
          className="modal"
          PaperProps={{
            sx: {
              backgroundColor: 'rgba(255, 255, 255, 0.98)',
              backdropFilter: 'blur(10px)',
            }
          }}
          BackdropProps={{
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
            }
          }}
        >
          <DialogTitle sx={{ color: '#1f2937' }}>
            {editUser ? "Edit User" : "Add User"}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
              <TextField
                label="Email"
                type="email"
                value={editUser ? editUser.email : newUser.email}
                onChange={(e) =>
                  editUser
                    ? setEditUser({ ...editUser, email: e.target.value })
                    : setNewUser({ ...newUser, email: e.target.value })
                }
                fullWidth
                required
                autoComplete="off"
                InputProps={{
                  style: {
                    backgroundColor: '#ffffff',
                    color: '#1f2937',
                  }
                }}
                InputLabelProps={{
                  style: {
                    color: 'rgba(31, 41, 55, 0.7)',
                  }
                }}
              />
              <TextField
                label="Full Name"
                value={editUser ? editUser.full_name : newUser.full_name}
                onChange={(e) =>
                  editUser
                    ? setEditUser({ ...editUser, full_name: e.target.value })
                    : setNewUser({ ...newUser, full_name: e.target.value })
                }
                fullWidth
                required
                autoComplete="off"
                InputProps={{
                  style: {
                    backgroundColor: '#ffffff',
                    color: '#1f2937',
                  }
                }}
                InputLabelProps={{
                  style: {
                    color: 'rgba(31, 41, 55, 0.7)',
                  }
                }}
              />
              {!editUser && (
                <>
                  <TextField
                    label="Password"
                    type="password"
                    value={newUser.password}
                    onChange={(e) =>
                      setNewUser({ ...newUser, password: e.target.value })
                    }
                    fullWidth
                    required
                    autoComplete="new-password"
                    InputProps={{
                      style: {
                        backgroundColor: '#ffffff',
                        color: '#1f2937',
                      }
                    }}
                    InputLabelProps={{
                      style: {
                        color: 'rgba(31, 41, 55, 0.7)',
                      }
                    }}
                  />
                  <FormControl fullWidth>
                    <InputLabel style={{ color: 'rgba(31, 41, 55, 0.7)' }}>Role</InputLabel>
                    <Select
                      value={newUser.is_superuser}
                      onChange={(e) =>
                        setNewUser((prev) => ({
                          ...prev,
                          is_superuser: e.target.value,
                        }))
                      }
                      label="Role"
                      autoComplete="off"
                      style={{
                        backgroundColor: '#ffffff',
                        color: '#1f2937',
                      }}
                    >
                      <MenuItem value="False">User</MenuItem>
                      <MenuItem value="True">Admin</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl fullWidth>
                    <InputLabel style={{ color: 'rgba(31, 41, 55, 0.7)' }}>Department</InputLabel>
                    <Select
                      value={newUser.department || "Compliance"}
                      onChange={(e) =>
                        setNewUser((prev) => ({
                          ...prev,
                          department: e.target.value,
                        }))
                      }
                      label="Department"
                      autoComplete="off"
                      style={{
                        backgroundColor: '#ffffff',
                        color: '#1f2937',
                      }}
                    >
                      {departments.map((dept) => (
                        <MenuItem key={dept} value={dept}>
                          {dept}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth>
                    <InputLabel style={{ color: 'rgba(31, 41, 55, 0.7)' }}>Status</InputLabel>
                    <Select
                      value={newUser.is_active}
                      onChange={(e) =>
                        setNewUser((prev) => ({
                          ...prev,
                          is_active: e.target.value,
                        }))
                      }
                      label="Status"
                      autoComplete="off"
                      style={{
                        backgroundColor: '#ffffff',
                        color: '#1f2937',
                      }}
                    >
                      <MenuItem value="True">Active</MenuItem>
                      <MenuItem value="False">Inactive</MenuItem>
                    </Select>
                  </FormControl>
                  <Button
                    variant="contained"
                    className="btn-contained"
                    onClick={() => createTidToken(newUser.is_superuser)}
                    disabled={loading || !!tid}
                    fullWidth
                  >
                    {loading
                      ? "Generating..."
                      : tid
                      ? "Token ID Generated ✓"
                      : "Generate Token ID"}
                  </Button>
                  {tid && (
                    <TextField
                      label="Token ID"
                      value={tid}
                      slotProps={{
                        input: {
                          readOnly: true,
                          endAdornment: (
                            <InputAdornment position="end">
                              <Button
                                variant="text"
                                size="small"
                                onClick={handleCopyTid}
                                disabled={loading}
                                sx={{ minWidth: "auto", p: 0.5 }}
                              >
                                <ContentCopyIcon fontSize="small" />
                              </Button>
                            </InputAdornment>
                          ),
                          style: {
                            backgroundColor: '#ffffff',
                            color: '#10b981',
                          }
                        },
                      }}
                      fullWidth
                      autoComplete="off"
                      helperText="Copy this Token ID to proceed."
                      InputLabelProps={{
                        style: {
                          color: 'rgba(16, 185, 129, 0.9)',
                        }
                      }}
                    />
                  )}
                </>
              )}
              {editUser && (
                <>
                  <FormControl fullWidth>
                    <InputLabel style={{ color: 'rgba(31, 41, 55, 0.7)' }}>Role</InputLabel>
                    <Select
                      value={editUser.is_superuser === "True" ? "True" : "False"}
                      onChange={(e) =>
                        setEditUser((prev) => ({
                          ...prev,
                          is_superuser: e.target.value,
                        }))
                      }
                      label="Role"
                      autoComplete="off"
                      style={{
                        backgroundColor: '#ffffff',
                        color: '#1f2937',
                      }}
                    >
                      <MenuItem value="False">User</MenuItem>
                      <MenuItem value="True">Admin</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl fullWidth>
                    <InputLabel style={{ color: 'rgba(31, 41, 55, 0.7)' }}>Department</InputLabel>
                    <Select
                      value={editUser.department || "Compliance"}
                      onChange={(e) =>
                        setEditUser((prev) => ({
                          ...prev,
                          department: e.target.value,
                        }))
                      }
                      label="Department"
                      autoComplete="off"
                      style={{
                        backgroundColor: '#ffffff',
                        color: '#1f2937',
                      }}
                    >
                      {departments.map((dept) => (
                        <MenuItem key={dept} value={dept}>
                          {dept}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth>
                    <InputLabel style={{ color: 'rgba(31, 41, 55, 0.7)' }}>Status</InputLabel>
                    <Select
                      value={editUser.is_active === "True" ? "True" : "False"}
                      onChange={(e) =>
                        setEditUser((prev) => ({
                          ...prev,
                          is_active: e.target.value,
                        }))
                      }
                      label="Status"
                      autoComplete="off"
                      style={{
                        backgroundColor: '#ffffff',
                        color: '#1f2937',
                      }}
                    >
                      <MenuItem value="True">Active</MenuItem>
                      <MenuItem value="False">Inactive</MenuItem>
                    </Select>
                  </FormControl>
                </>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setOpenUserModal(false);
                setEditUser(null);
                setTid("");
                if (!editUser) {
                  setNewUser({
                    email: "",
                    full_name: "",
                    is_superuser: "False",
                    is_active: "True",
                    password: "",
                    tid_token: "",
                    department: "Compliance",
                  });
                }
              }}
              variant="outlined"
              className="btn-outlined"
            >
              Cancel
            </Button>
            <Button
              onClick={editUser ? handleEditUser : handleAddUser}
              disabled={loading || (!editUser && !tid)}
              variant="contained"
              className="btn-contained"
            >
              {loading ? "Processing..." : editUser ? "Save" : "Add"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Notification Modal */}
        <Dialog
          open={openNotifModal}
          onClose={() => setOpenNotifModal(false)}
          maxWidth="sm"
          fullWidth
          className="modal"
          PaperProps={{
            sx: {
              backgroundColor: 'rgba(255, 255, 255, 0.98)',
              backdropFilter: 'blur(10px)',
            }
          }}
          BackdropProps={{
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
            }
          }}
        >
          <DialogTitle sx={{ color: '#1f2937' }}>Send Notification</DialogTitle>
          <DialogContent>
            <TextField
              label="Message"
              value={notifMessage}
              onChange={(e) => setNotifMessage(e.target.value)}
              fullWidth
              multiline
              rows={4}
              sx={{ mt: 2 }}
              InputProps={{
                style: {
                  backgroundColor: '#ffffff',
                  color: '#1f2937',
                }
              }}
              InputLabelProps={{
                style: {
                  color: 'rgba(31, 41, 55, 0.7)',
                }
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setOpenNotifModal(false)}
              variant="outlined"
              className="btn-outlined"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSendNotification}
              variant="contained"
              className="btn-contained"
            >
              Send
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
      <ToastContainer />
    </Box>
  );
};

export default AdminPanel;
                        